import type { AuthUser } from '@hemmafixa/shared';
import { navigation, type Area } from '@/app/navigation';

/** Which area someone lands in after login: the most specific role they have. */
export function homeArea(user: AuthUser): Area {
  if (user.roles.includes('admin')) return 'admin';
  if (user.roles.includes('provider')) return 'provider';
  return 'client';
}

export function homePath(user: AuthUser) {
  return navigation[homeArea(user)][0]!.path;
}

export function canUse(user: AuthUser, area: Area) {
  return user.roles.includes(area);
}

/**
 * The page to open after login, from `?next=`. Only paths inside this app: anything else (another site,
 * `//evil.example`, `/\\evil.example`) is ignored, so a login link cannot send people elsewhere.
 */
export function safeNext(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null;
  // Control characters (a newline, a tab) have no place in a path
  if ([...value].some((char) => char.charCodeAt(0) < 0x20)) return null;
  return value;
}

/** Pages outside the areas that any signed-in user may return to after login */
const SIGNED_IN_PAGES = ['/provider-signup'];

/** Where to go after login: `next` if this user may open it, otherwise their home area. */
export function afterLogin(user: AuthUser, next: string | null) {
  const path = safeNext(next);
  if (path && SIGNED_IN_PAGES.includes(path)) return path;
  const area = path?.split('/')[1] as Area | undefined;
  if (path && area && navigation[area] && canUse(user, area)) return path;
  return homePath(user);
}

/** "Anna Lindqvist" → "Anna", for greetings. */
export function firstName(name: string) {
  return name.trim().split(/\s+/)[0] ?? name;
}
